import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/store";
import { useColWidths } from "@/hooks/useColWidths";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  ArrowLeft, Loader2, FunctionSquare, LockKeyhole, Building2, Users, Save, Settings2,
  FileSpreadsheet, FileText, Pencil, RefreshCw, Plus, Trash2, RotateCcw
} from "lucide-react";
import { toast } from "sonner";
import { t } from "@/i18n/bg";

function getColLetter(col) {
  let s = "", c = col;
  while (c >= 0) { s = String.fromCharCode((c % 26) + 65) + s; c = Math.floor(c / 26) - 1; }
  return s;
}

export default function ConsolidatedReportPage() {
  const { periodId } = useParams();
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [sheets, setSheets] = useState([]);
  const [activeSheet, setActiveSheet] = useState(null);
  const [cells, setCells] = useState([]);
  const [mergedCells, setMergedCells] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [recalcCells, setRecalcCells] = useState({}); // sheet -> Set of addresses
  const [noSumCells, setNoSumCells] = useState({}); // sheet -> Set of addresses
  const [conFormulas, setConFormulas] = useState({}); // sheet -> {address -> formula}
  const [showFormulaPanel, setShowFormulaPanel] = useState(false);
  const [newFormulaSheet, setNewFormulaSheet] = useState("");
  const [newFormulaAddr, setNewFormulaAddr] = useState("");
  const [newFormulaExpr, setNewFormulaExpr] = useState("");
  const [hasSettingsChanges, setHasSettingsChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cellDialog, setCellDialog] = useState(null); // { address, sheet, value, formula }
  const [cellSaving, setCellSaving] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const gridRef = useRef(null);
  const { handleResizeStart, getColStyle } = useColWidths(
    periodId ? `consolidated:${periodId}` : null, activeSheet
  );

  useEffect(() => { loadMeta(); }, [periodId]);
  useEffect(() => { if (activeSheet && meta) loadSheet(activeSheet); }, [activeSheet]);

  const loadMeta = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/consolidated/${periodId}`);
      setMeta(res.data);
      setSheets(res.data.sheets || []);
      // Load settings
      if (res.data.template?.id) {
        const sRes = await api.get(`/templates/${res.data.template.id}/consolidated-settings`);
        const rc = {};
        for (const [sn, addrs] of Object.entries(sRes.data.recalc_cells || {})) {
          rc[sn] = new Set(addrs);
        }
        setRecalcCells(rc);
        const ns = {};
        for (const [sn, addrs] of Object.entries(sRes.data.no_sum_cells || {})) {
          ns[sn] = new Set(addrs);
        }
        setNoSumCells(ns);
        setConFormulas(sRes.data.con_formulas || {});
      }
      if (res.data.sheets?.length > 0) setActiveSheet(res.data.sheets[0].sheet_name);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("loadFailed"));
      navigate("/dashboard");
    } finally { setLoading(false); }
  };

  const loadSheet = async (sheetName) => {
    try {
      setLoading(true);
      const res = await api.get(`/consolidated/${periodId}/sheets/${encodeURIComponent(sheetName)}`);
      setCells(res.data.cells || []);
      setMergedCells(res.data.merged_cells || []);
    } catch { toast.error(t("loadFailed")); }
    finally { setLoading(false); }
  };

  const toggleCellMode = (addr, isFormula) => {
    if (!editMode) return;
    const sn = activeSheet;
    // Cycle: Normal(Sum) → NoSum → Recalc(formula only) → Normal
    // For non-formula cells: Normal(Sum) → NoSum → Normal
    const inRecalc = recalcCells[sn]?.has(addr);
    const inNoSum = noSumCells[sn]?.has(addr);

    if (!inRecalc && !inNoSum) {
      // Currently summing → switch to NoSum
      setNoSumCells(prev => {
        const next = { ...prev };
        next[sn] = new Set(next[sn] || []);
        next[sn].add(addr);
        return next;
      });
    } else if (inNoSum) {
      // Currently NoSum → switch to Recalc (if formula) or back to Sum
      setNoSumCells(prev => {
        const next = { ...prev };
        next[sn] = new Set(next[sn] || []);
        next[sn].delete(addr);
        return next;
      });
      if (isFormula) {
        setRecalcCells(prev => {
          const next = { ...prev };
          next[sn] = new Set(next[sn] || []);
          next[sn].add(addr);
          return next;
        });
      }
    } else if (inRecalc) {
      // Currently Recalc → back to Sum
      setRecalcCells(prev => {
        const next = { ...prev };
        next[sn] = new Set(next[sn] || []);
        next[sn].delete(addr);
        return next;
      });
    }
    setHasSettingsChanges(true);
  };

  const saveSettings = async () => {
    if (!meta?.template?.id) return;
    setSaving(true);
    try {
      const rcPayload = {};
      for (const [sn, addrSet] of Object.entries(recalcCells)) {
        if (addrSet.size > 0) rcPayload[sn] = [...addrSet];
      }
      const nsPayload = {};
      for (const [sn, addrSet] of Object.entries(noSumCells)) {
        if (addrSet.size > 0) nsPayload[sn] = [...addrSet];
      }
      await api.put(`/templates/${meta.template.id}/consolidated-settings`, {
        recalc_cells: rcPayload, no_sum_cells: nsPayload, con_formulas: conFormulas,
      });
      toast.success(t("recalcCellsSaved"));
      setHasSettingsChanges(false);
      loadSheet(activeSheet);
    } catch (e) { toast.error(e.response?.data?.detail || t("failed")); }
    finally { setSaving(false); }
  };

  // ─── Cell Edit for Consolidated Report ───
  const handleCellDblClick = (cell) => {
    if (!cell || editMode) return; // editMode is for sum/nosum settings
    setCellDialog({
      address: cell.address,
      sheet: activeSheet,
      value: cell.value ?? "",
      formula: cell.formula ?? "",
      hasOverride: cell.has_con_override || false,
    });
  };

  const handleCellEditSave = async () => {
    if (!cellDialog) return;
    setCellSaving(true);
    try {
      await api.patch(`/consolidated/${periodId}/cells`, {
        sheet_name: cellDialog.sheet,
        address: cellDialog.address,
        value: cellDialog.value,
        formula: cellDialog.formula || null,
      });
      setCellDialog(null);
      toast.success(`${cellDialog.address} ${t("cellSaved")}`);
      loadSheet(activeSheet);
    } catch (e) { toast.error(e.response?.data?.detail || t("failed")); }
    finally { setCellSaving(false); }
  };

  const handleCellEditRemove = async () => {
    if (!cellDialog) return;
    setCellSaving(true);
    try {
      await api.delete(`/consolidated/${periodId}/overrides/${encodeURIComponent(cellDialog.sheet)}/${cellDialog.address}`);
      setCellDialog(null);
      toast.success(t("overrideRemoved"));
      loadSheet(activeSheet);
    } catch (e) { toast.error(e.response?.data?.detail || t("failed")); }
    finally { setCellSaving(false); }
  };

  const handleRecalculate = async () => {
    setRecalculating(true);
    try {
      const res = await api.post(`/consolidated/${periodId}/recalculate`);
      toast.success(`${t("recalculating")} (${res.data.count} ${t("reports")})`);
      // Wait a few seconds for background recalculation to complete, then reload
      setTimeout(() => {
        loadSheet(activeSheet);
        setRecalculating(false);
      }, 3000);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("failed"));
      setRecalculating(false);
    }
  };

  const addConFormula = () => {
    const sheet = newFormulaSheet || activeSheet;
    const addr = newFormulaAddr.trim().toUpperCase();
    const formula = newFormulaExpr.trim();
    if (!sheet || !addr || !formula) { toast.error("Попълнете всички полета"); return; }
    if (!/^[A-Z]+\d+$/.test(addr)) { toast.error("Невалиден адрес на клетка (пр. D15)"); return; }
    setConFormulas(prev => {
      const next = { ...prev };
      if (!next[sheet]) next[sheet] = {};
      next[sheet] = { ...next[sheet], [addr]: formula };
      return next;
    });
    setHasSettingsChanges(true);
    setNewFormulaAddr("");
    setNewFormulaExpr("");
    toast.success(`Формула за ${sheet}!${addr} добавена`);
  };

  const removeConFormula = (sheet, addr) => {
    setConFormulas(prev => {
      const next = { ...prev };
      if (next[sheet]) {
        const { [addr]: _, ...rest } = next[sheet];
        next[sheet] = rest;
        if (Object.keys(next[sheet]).length === 0) delete next[sheet];
      }
      return next;
    });
    setHasSettingsChanges(true);
  };

  if (loading && !meta) return (
    <div className="flex items-center justify-center h-screen bg-[#f8fafc]">
      <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
    </div>
  );

  const handleExport = async (format) => {
    try {
      const res = await api.get(`/consolidated/${periodId}/export`, { params: { format }, responseType: "blob" });
      const ext = format === "xlsx" ? "xlsx" : "pdf";
      const a = document.createElement("a");
      a.href = window.URL.createObjectURL(new Blob([res.data]));
      a.download = `consolidated_${meta?.period?.name || "report"}.${ext}`;
      a.click();
      toast.success(t("downloaded"));
    } catch { toast.error(t("exportFailed")); }
  };

  const maxRow = cells.length > 0 ? Math.max(...cells.map(c => c.row)) : 0;
  const maxCol = cells.length > 0 ? Math.max(...cells.map(c => c.col)) : 0;
  const cellMap = {};
  cells.forEach(c => { cellMap[`${c.row},${c.col}`] = c; });
  const currentRecalc = recalcCells[activeSheet] || new Set();
  const currentNoSum = noSumCells[activeSheet] || new Set();

  const mergeMap = {};
  mergedCells.forEach(mc => {
    try {
      const sr = mc.min_row, sc = mc.min_col, er = mc.max_row, ec = mc.max_col;
      if (!sr || !sc || !er || !ec || sr > er || sc > ec) return;
      mergeMap[`${sr},${sc}`] = { rowSpan: er - sr + 1, colSpan: ec - sc + 1 };
      for (let r = sr; r <= er; r++) for (let c = sc; c <= ec; c++) {
        if (r === sr && c === sc) continue;
        mergeMap[`${r},${c}`] = "hidden";
      }
    } catch {}
  });

  return (
    <div data-testid="consolidated-report" className="flex flex-col h-screen bg-[#f8fafc]">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-white border-b border-slate-200 shadow-sm shrink-0">
        <div className="flex items-center gap-3">
          <Button data-testid="back-to-dashboard" variant="ghost" size="sm" onClick={() => navigate("/dashboard")} className="h-8 text-slate-600">
            <ArrowLeft className="w-4 h-4 mr-1" /> {t("back")}
          </Button>
          <div className="h-5 w-px bg-slate-200" />
          <Building2 className="w-4 h-4 text-[#14532d]" />
          <span className="text-sm font-semibold text-[#14532d]">{t("consolidatedReport")}</span>
          <span className="text-xs text-slate-500">{meta?.period?.name}</span>
          <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-indigo-50 text-indigo-700 border border-indigo-200">
            <Users className="w-3 h-3 mr-1" /> {meta?.unit_count} {t("unitsIncluded")}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="recalculate-consolidated" variant="outline" size="sm"
            onClick={handleRecalculate} disabled={recalculating}
            className="h-8 text-xs text-amber-700 border-amber-200 hover:bg-amber-50">
            {recalculating ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
            {t("recalculate")}
          </Button>
          <div className="w-px h-6 bg-slate-200" />
          <Button data-testid="export-consolidated-xlsx" variant="outline" size="sm" onClick={() => handleExport("xlsx")} className="h-8 text-xs">
            <FileSpreadsheet className="w-3.5 h-3.5 mr-1" /> XLSX
          </Button>
          <Button data-testid="export-consolidated-pdf" variant="outline" size="sm" onClick={() => handleExport("pdf")} className="h-8 text-xs">
            <FileText className="w-3.5 h-3.5 mr-1" /> PDF
          </Button>
          <div className="w-px h-6 bg-slate-200" />
          <Button data-testid="toggle-settings-mode" size="sm"
            variant={editMode ? "default" : "outline"}
            onClick={() => setEditMode(!editMode)}
            className={`h-8 text-xs ${editMode ? "bg-violet-600 hover:bg-violet-700 text-white" : "text-violet-600 border-violet-200 hover:bg-violet-50"}`}>
            <Settings2 className="w-3.5 h-3.5 mr-1" /> {t("consolidatedSettings")}
          </Button>
          {hasSettingsChanges && (
            <Button data-testid="save-consolidated-settings" size="sm" onClick={saveSettings} disabled={saving}
              className="h-8 text-xs bg-[#14532d] hover:bg-[#166534] text-white">
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
              {t("save")}
            </Button>
          )}
        </div>
      </div>

      {/* Edit mode banner */}
      {editMode && (
        <div className="flex items-center justify-between gap-3 px-4 py-2 bg-violet-50 border-b border-violet-200 text-xs text-violet-700 shrink-0">
          <div className="flex items-center gap-2">
            <Settings2 className="w-4 h-4" />
            <span>Кликнете върху клетка за да превключите: <strong>Сума</strong> → <strong style={{color:"#dc2626"}}>Без сума</strong> → {" "}
            <strong style={{color:"#7c3aed"}}>Формула</strong> (само за формули) → Сума.</span>
          </div>
          <Button data-testid="toggle-formula-panel" size="sm" variant="outline"
            onClick={() => setShowFormulaPanel(!showFormulaPanel)}
            className={`h-7 text-[11px] ${showFormulaPanel ? "bg-amber-100 text-amber-800 border-amber-300" : "text-amber-700 border-amber-200"}`}>
            <FunctionSquare className="w-3.5 h-3.5 mr-1" />
            Специални формули ({Object.values(conFormulas).reduce((s, v) => s + Object.keys(v).length, 0)})
          </Button>
        </div>
      )}

      {/* Special Formulas Panel (consolidated-only) */}
      {editMode && showFormulaPanel && (
        <div data-testid="con-formula-panel" className="bg-amber-50 border-b border-amber-200 px-4 py-3 shrink-0 max-h-[260px] overflow-y-auto">
          <div className="flex items-center gap-2 mb-2">
            <FunctionSquare className="w-4 h-4 text-amber-700" />
            <span className="text-xs font-semibold text-amber-800">Специални формули за консолидирания отчет</span>
            <span className="text-[10px] text-amber-600 ml-1">Тези формули се изпълняват САМО в консолидирания отчет</span>
          </div>

          {/* Add new formula */}
          <div className="flex items-end gap-2 mb-3 bg-white/60 rounded-lg p-2 border border-amber-200">
            <div className="flex-shrink-0 w-36">
              <label className="text-[10px] font-medium text-amber-700 block mb-0.5">Лист</label>
              <select data-testid="con-formula-sheet-select" value={newFormulaSheet || activeSheet || ""}
                onChange={(e) => setNewFormulaSheet(e.target.value)}
                className="w-full h-7 text-xs border border-amber-200 rounded px-1.5 bg-white">
                {sheets.map(s => <option key={s.sheet_name} value={s.sheet_name}>{s.sheet_name}</option>)}
              </select>
            </div>
            <div className="flex-shrink-0 w-20">
              <label className="text-[10px] font-medium text-amber-700 block mb-0.5">Клетка</label>
              <input data-testid="con-formula-addr-input" value={newFormulaAddr}
                onChange={(e) => setNewFormulaAddr(e.target.value)}
                placeholder="D15" className="w-full h-7 text-xs border border-amber-200 rounded px-1.5 font-mono bg-white" />
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="text-[10px] font-medium text-amber-700 block mb-0.5">Формула</label>
              <input data-testid="con-formula-expr-input" value={newFormulaExpr}
                onChange={(e) => setNewFormulaExpr(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addConFormula()}
                placeholder="=SUM(D10:D14)*0.2" className="w-full h-7 text-xs border border-amber-200 rounded px-1.5 font-mono bg-white" />
            </div>
            <Button data-testid="add-con-formula-btn" size="sm" onClick={addConFormula}
              className="h-7 bg-amber-600 hover:bg-amber-700 text-white text-[11px] flex-shrink-0">
              <Plus className="w-3.5 h-3.5 mr-0.5" /> Добави
            </Button>
          </div>

          {/* List of existing formulas */}
          {Object.keys(conFormulas).length > 0 ? (
            <div className="space-y-1">
              {Object.entries(conFormulas).map(([sheetName, formulas]) =>
                Object.entries(formulas).map(([addr, formula]) => (
                  <div key={`${sheetName}-${addr}`} data-testid={`con-formula-${sheetName}-${addr}`}
                    className="flex items-center gap-2 bg-white/80 rounded px-2 py-1 border border-amber-100 text-xs">
                    <span className="font-mono text-amber-800 font-medium w-40 truncate">{sheetName}</span>
                    <span className="font-mono text-amber-900 font-semibold w-14">{addr}</span>
                    <span className="font-mono text-slate-700 flex-1 truncate">{formula}</span>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400 hover:text-red-600 hover:bg-red-50"
                      onClick={() => removeConFormula(sheetName, addr)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))
              )}
            </div>
          ) : (
            <p className="text-[11px] text-amber-500 italic">Няма добавени специални формули</p>
          )}
        </div>
      )}

      {/* Sheet tabs */}
      <div className="flex items-center bg-white border-b border-slate-200 px-2 overflow-x-auto shrink-0">
        {sheets.map(s => (
          <button key={s.sheet_name} data-testid={`con-sheet-${s.order_index}`}
            onClick={() => setActiveSheet(s.sheet_name)}
            className={`px-4 py-2.5 text-xs font-medium whitespace-nowrap border-b-2 transition-colors ${
              activeSheet === s.sheet_name ? "border-indigo-600 text-indigo-700 bg-indigo-50/50" : "border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50"
            }`}>{s.sheet_name}</button>
        ))}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 px-4 py-1.5 bg-white border-b border-slate-100 text-[10px] text-slate-500 shrink-0">
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded border border-indigo-300 bg-indigo-50 inline-block" /> {t("sumFromUnits")}</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded border border-red-300 bg-red-50 inline-block" /> {t("noSumSkipped")}</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded border border-violet-300 bg-violet-100 inline-block" /> {t("recalcFromFormula")}</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded border border-amber-400 bg-amber-50 inline-block" /> Специална формула</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded border border-slate-200 bg-slate-100 inline-block" /> {t("formulaAutoCalc")}</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded border border-slate-200 bg-white inline-block" /> {t("static")}</span>
      </div>

      {/* Grid */}
      <div data-testid="consolidated-grid" className="flex-1 overflow-auto" ref={gridRef}>
        <TooltipProvider delayDuration={200}>
        {loading ? (
          <div className="flex items-center justify-center h-64"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
        ) : cells.length === 0 ? (
          <div className="flex items-center justify-center h-64 text-muted-foreground">{t("noData")}</div>
        ) : (
          <table className="border-collapse text-xs" style={{ fontFamily: "'Calibri', 'Segoe UI', sans-serif", tableLayout: "fixed" }}>
            <thead className="sticky top-0 z-10">
              <tr>
                <th className="bg-slate-100 border border-slate-200 px-1 py-1 text-center text-slate-400 font-normal w-10 min-w-[40px]">#</th>
                {Array.from({ length: maxCol }, (_, i) => (
                  <th key={i} className="bg-slate-100 border border-slate-200 px-2 py-1 text-center text-slate-500 font-semibold relative select-none"
                    style={{ fontSize: "11px", ...getColStyle(i) }}>
                    {getColLetter(i)}
                    <div onMouseDown={(e) => handleResizeStart(i, e)}
                      className="absolute top-0 right-0 w-1.5 h-full cursor-col-resize hover:bg-indigo-500/30 active:bg-indigo-500/50"
                      style={{ touchAction: "none" }} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: maxRow }, (_, rowIdx) => {
                const row = rowIdx + 1;
                return (
                  <tr key={row}>
                    <td className="bg-slate-50 border border-slate-200 px-1 py-0.5 text-center text-slate-400 font-normal" style={{ fontSize: "10px" }}>{row}</td>
                    {Array.from({ length: maxCol }, (_, colIdx) => {
                      const col = colIdx + 1;
                      const mi = mergeMap[`${row},${col}`];
                      if (mi === "hidden") return null;
                      const cell = cellMap[`${row},${col}`];
                      const isMerge = mi && mi !== "hidden";
                      const isRecalc = cell && (cell.is_recalc || currentRecalc.has(cell.address));
                      const isNoSum = cell && (cell.is_no_sum || currentNoSum.has(cell.address));
                      const isConFormula = cell?.is_con_formula;
                      let bg = "#ffffff", textColor = "#1e293b", tooltip = "";

                      if (cell) {
                        if (isConFormula) {
                          bg = "#fffbeb"; textColor = "#92400e";
                          tooltip = `Специална формула: ${cell.formula || ""}`;
                        } else if (isNoSum) {
                          bg = "#fef2f2"; textColor = "#991b1b"; // red tint for no-sum
                          tooltip = t("noSumSkipped");
                        } else if (isRecalc) {
                          bg = "#ede9fe"; // violet for recalc-from-formula
                          tooltip = `${t("recalcFromFormula")}: ${cell.formula || ""}`;
                        } else if (cell.unit_count > 0 && !cell.is_formula) {
                          bg = "#eef2ff";
                          tooltip = `${t("sumFromUnits")}: ${cell.unit_count}`;
                        } else if (cell.is_formula) {
                          bg = "#f1f5f9"; textColor = "#475569";
                          tooltip = `${t("formula")}: ${cell.formula}`;
                        } else if (cell.is_protected_preset) {
                          bg = "#fffbeb"; textColor = "#78716c";
                        }
                        if (cell.style?.bold) textColor = "#0f172a";
                      }

                      // In edit mode, highlight ALL cells as clickable
                      if (editMode && cell) {
                        if (isNoSum) bg = "#fca5a5";
                        else if (isRecalc) bg = "#c4b5fd";
                        else bg = "#e0e7ff";
                      }

                      let dv = cell?.value ?? "";
                      if (dv === "None") dv = "";
                      if (typeof dv === "string" && dv !== "" && !isNaN(dv)) {
                        const num = parseFloat(dv);
                        if (!isNaN(num)) dv = Number.isInteger(num) ? num.toLocaleString("en-US").replace(/,/g, " ") : num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).replace(/,/g, " ");
                      }

                      const hasOverride = cell?.has_con_override;

                      const td = (
                        <td key={col}
                          onClick={() => editMode && cell && toggleCellMode(cell.address, cell.is_formula)}
                          onDoubleClick={() => !editMode && cell && handleCellDblClick(cell)}
                          rowSpan={isMerge ? mi.rowSpan : undefined}
                          colSpan={isMerge ? mi.colSpan : undefined}
                          className={`border border-slate-200 px-1.5 py-0.5 relative ${editMode && cell ? "cursor-pointer hover:ring-2 hover:ring-violet-400 hover:ring-inset" : !editMode && cell ? "cursor-pointer hover:bg-slate-50" : ""}`}
                          style={{
                            backgroundColor: hasOverride ? "#fef3c7" : bg, color: textColor,
                            fontWeight: cell?.style?.fontWeight === "bold" || cell?.style?.bold ? "700" : cell?.style?.fontWeight === "semibold" ? "600" : "normal",
                            fontSize: cell?.style?.fontSize ? `${Math.min(cell.style.fontSize, 16)}px` : undefined,
                            textAlign: cell?.style?.align || ((cell && !isNaN(cell.value) && cell.value !== "" && cell.value !== null && cell.value !== "None") ? "right" : "left"),
                            minWidth: "80px", maxWidth: isMerge ? undefined : "200px",
                            ...(!isMerge ? getColStyle(col - 1) : {}),
                            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                            borderLeft: hasOverride ? "2px solid #f59e0b" : isConFormula ? "2px solid #d97706" : isNoSum ? "2px solid #ef4444" : isRecalc ? "2px solid #8b5cf6" : cell?.unit_count > 0 && !cell?.is_formula ? "2px solid #818cf8" : undefined,
                          }}
                        >
                          {dv}
                          {cell?.is_formula && <FunctionSquare className="w-2.5 h-2.5 absolute top-0 right-0 text-slate-300" />}
                          {hasOverride && <Pencil className="w-2.5 h-2.5 absolute top-0 right-0 text-amber-500" />}
                          {isConFormula && !hasOverride && <FunctionSquare className="w-2.5 h-2.5 absolute top-0 right-0 text-amber-600" />}
                          {isRecalc && <span className="absolute bottom-0 left-0 text-[7px] text-violet-500 px-0.5">f(x)</span>}
                          {isNoSum && <span className="absolute bottom-0 left-0 text-[7px] text-red-500 px-0.5">--</span>}
                        </td>
                      );

                      if (tooltip && !editMode) {
                        return (
                          <Tooltip key={col}>
                            <TooltipTrigger asChild>{td}</TooltipTrigger>
                            <TooltipContent side="top" className="text-[10px] max-w-[200px]">{tooltip}</TooltipContent>
                          </Tooltip>
                        );
                      }
                      return td;
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        </TooltipProvider>
      </div>

      {/* Cell Edit Dialog */}
      <Dialog open={!!cellDialog} onOpenChange={open => { if (!open) setCellDialog(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Pencil className="w-4 h-4 text-amber-600" />
              <span className="font-mono">{cellDialog?.sheet}!{cellDialog?.address}</span>
              <span className="text-xs text-slate-400 font-normal">{t("consolidatedOnly")}</span>
            </DialogTitle>
          </DialogHeader>
          {cellDialog && (
            <div className="space-y-4 pt-1">
              {cellDialog.hasOverride && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-800">
                  <Pencil className="w-3.5 h-3.5 shrink-0" />
                  Тази клетка има ръчна промяна. Натиснете "Възстанови" за да се върнете към стандартното сумиране.
                </div>
              )}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-700">{t("cellValue")}</Label>
                <Input data-testid="con-edit-value" value={cellDialog.value}
                  onChange={e => setCellDialog(prev => ({ ...prev, value: e.target.value }))}
                  className="h-9 text-sm font-mono" placeholder="..." />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-700">
                  <FunctionSquare className="w-3 h-3 inline mr-1" />{t("formulaLabel")}
                </Label>
                <Input data-testid="con-edit-formula" value={cellDialog.formula}
                  onChange={e => setCellDialog(prev => ({ ...prev, formula: e.target.value }))}
                  className="h-9 text-sm font-mono" placeholder="=SUM(C10:C14)" />
              </div>
            </div>
          )}
          <DialogFooter className="flex justify-between">
            <Button data-testid="con-edit-reset" variant="outline" size="sm" onClick={handleCellEditRemove} disabled={cellSaving}
              className="text-amber-700 border-amber-300 bg-amber-50 hover:bg-amber-100 mr-auto">
              <RotateCcw className="w-3.5 h-3.5 mr-1" />
              {cellDialog?.hasOverride ? "Възстанови (премахни промяната)" : "Възстанови по подразбиране"}
            </Button>
            <div className="flex gap-2 ml-auto">
              <Button variant="outline" size="sm" onClick={() => setCellDialog(null)}>{t("close")}</Button>
              <Button data-testid="con-edit-save" size="sm" onClick={handleCellEditSave} disabled={cellSaving}
                className="bg-amber-600 hover:bg-amber-700 text-white">
                {cellSaving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                {t("save")}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
